import React from 'react';

import { cn } from '@/libs';
import { uiConfig } from '@/config';

import { FormFieldMapper } from '..';
import { Checkbox, Input } from '../inputs';
import {
  useClearOnNotApplicable,
  useFieldValue,
  useFieldWarning,
  useNotApplicableField,
} from '../../hooks';
import {
  DateRangeFormField,
  FormValidation,
  FormValidationValue,
} from '../../typings';

import './DateRangePresenter.scss';
import { parseDateFromString } from '@/forms/utils';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';
import { FieldWarningAlert } from './FieldWarningAlert';

type DateRangePresenterProps = {
  field: DateRangeFormField;
  disabled?: boolean;
};

export const DateRangePresenter: React.FC<DateRangePresenterProps> = ({
  field,
  disabled,
}) => {
  const mainContainerClasses = cn(
    'drp__container',
    uiConfig.grid.maxColumnsSpanClass,
  );
  const containerClasses = cn(
    'grid',
    'grid-cols-2',
    'gap-x-[20px]',
    'gap-y-[35px]',
  );

  const fromValue = useFieldValue(field.fields[0].name);

  const isFieldNA = useNotApplicableField(field.notApplicableField?.fieldId);

  useClearOnNotApplicable(field, isFieldNA);

  // Only the "to" date carries warnings. Gating NA once keeps its input and the banner in step.
  const toWarning = useFieldWarning(field.fields[1]);
  const warning = isFieldNA ? undefined : toWarning;

  return field.fields.length > 2 ? (
    <></>
  ) : (
    <div className={mainContainerClasses}>
      {field.alertMessage && (
        <FormFieldAlertMessage alertMessage={field.alertMessage} />
      )}
      {field.notApplicableField !== undefined && (
        <Checkbox
          name={field.notApplicableField.fieldId}
          label={field.notApplicableField.label}
          size="small"
        />
      )}

      {isFieldNA && field.naDetails && (
        <div className={containerClasses}>
          <FormFieldMapper field={field.naDetails} disabled={disabled} />
        </div>
      )}

      <div className={containerClasses}>
        {field.fields.map((df, idx) => {
          let minDateValidation: Date | undefined;
          let maxDateValidation: Date | undefined;

          if (idx === 0) {
            const minValueValidation = df.validations?.find(
              v => v.type === FormValidation.MinValue,
            ) as FormValidationValue | undefined;
            if (minValueValidation) {
              minDateValidation = parseDateFromString(
                minValueValidation.value as string,
              );
            }
          }

          if (idx === 1) {
            if (fromValue) {
              minDateValidation = parseDateFromString(fromValue as string);
            }

            const maxValueValidation = df.validations?.find(
              v => v.type === FormValidation.MaxValue,
            ) as FormValidationValue | undefined;
            if (maxValueValidation) {
              maxDateValidation = parseDateFromString(
                maxValueValidation.value as string,
              );
            }
          }

          return (
            // Note: this div is to prevent layout shifts by the react-datepicker component
            <div key={df.name}>
              <Input.Date
                name={df.name}
                label={df.label}
                placeholder={df.placeholder}
                required={df.validations?.some(
                  v => v.type === FormValidation.Required,
                )}
                disabled={disabled || isFieldNA || df.readonly}
                warning={idx === 1 && warning !== undefined}
                {...(minDateValidation
                  ? { minDate: minDateValidation }
                  : { minDate: undefined })}
                {...(maxDateValidation
                  ? { maxDate: maxDateValidation }
                  : { maxDate: undefined })}
              />
            </div>
          );
        })}
      </div>

      {/* Below both dates, not in the "to" cell: the grid stays two columns on phones. */}
      {warning && <FieldWarningAlert warning={warning} />}
    </div>
  );
};
